// Create a poll: a question + 2–10 options, optional multiple answers.
import React, { useState } from 'react';
import type { NewPoll } from '../types';

export const PollComposer: React.FC<{ onCreate: (poll: NewPoll) => void; onClose: () => void }> = ({ onCreate, onClose }) => {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [multi, setMulti] = useState(false);

  const setOpt = (i: number, v: string) => setOptions((o) => o.map((x, j) => (j === i ? v : x)));
  const addOpt = () => setOptions((o) => (o.length < 10 ? [...o, ''] : o));
  const removeOpt = (i: number) => setOptions((o) => (o.length > 2 ? o.filter((_, j) => j !== i) : o));
  const valid = question.trim().length > 0 && options.filter((o) => o.trim()).length >= 2;

  const create = () => {
    if (!valid) return;
    onCreate({
      question: question.trim(),
      options: options.filter((o) => o.trim()).map((text) => ({ text: text.trim() })),
      multi,
    });
    onClose();
  };

  const inputCls =
    'w-full rounded-lg border border-border bg-card px-2.5 py-1.5 text-sm text-charcoal placeholder:text-muted-foreground focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200';

  return (
    <div className="absolute bottom-full left-0 z-20 mb-2 w-80 rounded-xl border border-border bg-card p-3 shadow-lg">
      <div className="mb-2 text-xs font-semibold text-charcoal">New poll</div>
      <input value={question} onChange={(e) => setQuestion(e.target.value)} autoFocus placeholder="Ask a question…" className={`${inputCls} mb-2`} />
      <div className="space-y-1.5">
        {options.map((o, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <input value={o} onChange={(e) => setOpt(i, e.target.value)} placeholder={`Option ${i + 1}`} className={inputCls} />
            {options.length > 2 && (
              <button type="button" onClick={() => removeOpt(i)} aria-label="Remove option" className="flex-none text-muted-foreground hover:text-red-600">
                ✕
              </button>
            )}
          </div>
        ))}
      </div>
      {options.length < 10 && (
        <button type="button" onClick={addOpt} className="mt-1.5 text-xs font-medium text-primary-600 hover:text-primary-700">
          + Add option
        </button>
      )}
      <label className="mt-2 flex items-center gap-2 text-xs text-charcoal">
        <input type="checkbox" checked={multi} onChange={(e) => setMulti(e.target.checked)} /> Allow multiple answers
      </label>
      <div className="mt-2 flex gap-2">
        <button type="button" onClick={create} disabled={!valid} className="rounded-lg bg-primary-500 px-3 py-1 text-xs font-semibold text-white hover:bg-primary-600 disabled:opacity-40">
          Create poll
        </button>
        <button type="button" onClick={onClose} className="rounded-lg px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-muted">
          Cancel
        </button>
      </div>
    </div>
  );
};
